// === FEATURE: PlayerDock ===
// A persistent, draggable "music dock" mounted once at the App root. It hosts
// the site's compact StaticFM radio (the brushed-metal tuner) and a LAUNCHER for
// the Seraphim Bumper.
//
// The Seraphim Bumper (MusicBumper visualizer) NO LONGER lives inside the dock —
// it never fit the compact housing. Instead the dock shows a small launcher
// button ("SERAPHIM BUMPER ▸") that calls props.onOpenBumper() to open the
// dedicated full-screen immersive view (see AngelicBumperView). So the dock now
// hosts ONLY StaticFM (always shown) and stays fixed / draggable / minimizable.
//
// The dock is position:fixed so it FOLLOWS the page as you scroll, and is
// react-draggable so the user can park it anywhere via the rivet handle bar.
// A minimize/collapse affordance stows it down to its handle bar.
//
// Audio coordination is handled by the shared audioBus singleton — each player
// (StaticFM here, MusicBumper in its immersive view) is responsible for calling
// audioBus.claim/release itself, so launching + playing the bumper still pauses
// StaticFM and vice-versa.
//
// PLAY WHILE MINIMIZED: minimizing the dock must NEVER stop playback. The hosted
// StaticFM (and its synthesized engine + audioBus claim + analyser meter) stays
// MOUNTED across minimize — we shrink/hide its full cabinet with CSS only and
// hand it a `compact` flag so it renders its small MINIMIZED face instead. We do
// NOT conditionally unmount the audio component, so the radio keeps playing while
// the dock is stowed.
//
// z-index: the dock sits at 9000 — above page content + the FloorToolbar, but
// below the immersive bumper view (40000) and the crash / x-ray overlays
// (100000). It is parked in the lower-right so it clears the FloorToolbar tab.

import * as React from "react";
import { useState, useRef, useCallback } from "react";
import Draggable from "react-draggable";
import StaticFM from "../StaticFM/StaticFM";
import "./playerdock.css";

function PlayerDock({ sound = true, onOpenBumper }) {
  // Collapsed = stowed down to just the handle bar.
  const [collapsed, setCollapsed] = useState(false);

  // react-draggable needs a node ref to avoid findDOMNode (StrictMode-safe).
  const nodeRef = useRef(null);

  // Track whether the pointer actually dragged so a click on the (now huge,
  // whole-bar) collapsed handle expands the dock WITHOUT firing on the tail of a
  // drag. We watch the drag delta; anything past a few px counts as a drag, so a
  // real tap still opens the dock.
  const draggedRef = useRef(false);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  const onDragStart = useCallback(() => {
    draggedRef.current = false;
  }, []);
  const onDrag = useCallback((_e, data) => {
    if (Math.abs(data.deltaX) > 0 || Math.abs(data.deltaY) > 0) {
      draggedRef.current = true;
    }
  }, []);

  // Whole collapsed bar acts as a giant "open" affordance. Ignore the click that
  // trails a drag so parking the dock never accidentally expands it. The explicit
  // toggle button still works in both states (it stops propagation below).
  const onHandleClick = useCallback(() => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    if (collapsed) setCollapsed(false);
  }, [collapsed]);

  // The explicit toggle button owns its own click; keep it from also triggering
  // the whole-bar handler so a tap on it doesn't double-fire.
  const onToggleClick = useCallback(
    (e) => {
      e.stopPropagation();
      toggleCollapsed();
    },
    [toggleCollapsed]
  );

  // Launch the dedicated full-screen Seraphim Bumper experience. The bumper is
  // NOT mounted inside the dock anymore — App owns the overlay state.
  const launchBumper = useCallback(() => {
    if (typeof onOpenBumper === "function") onOpenBumper();
  }, [onOpenBumper]);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".pdock-handle"
      bounds="body"
      cancel=".pdock-launch, .pdock-min"
      onStart={onDragStart}
      onDrag={onDrag}
    >
      <div
        ref={nodeRef}
        className={"pdock is-fm" + (collapsed ? " is-collapsed" : "")}
        role="region"
        aria-label="Music dock"
      >
        {/* Brushed-metal handle bar: drag from here. Holds the brand plate,
            the bumper launcher, and the minimize affordance.

            The WHOLE bar is the open/minimize affordance: while collapsed a click
            anywhere on it expands the dock (giant hit area), and the labelled
            toggle pad at the end flips it either way. Dragging to park the dock is
            still owned by react-draggable on this same element — a trailing click
            after a drag is ignored (see onHandleClick). */}
        <div
          className="pdock-handle"
          onClick={onHandleClick}
          role={collapsed ? "button" : undefined}
          tabIndex={collapsed ? 0 : undefined}
          aria-label={collapsed ? "Open music dock" : undefined}
          aria-expanded={collapsed ? false : undefined}
          onKeyDown={
            collapsed
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setCollapsed(false);
                  }
                }
              : undefined
          }
        >
          {/* Rivets echo the rest of the site's chrome */}
          <span className="pdock-rivot pdock-rivot-l" aria-hidden="true" />

          <span className="pdock-brand">
            <span className="pdock-grip" aria-hidden="true" />
            MUSIC&middot;DOCK
          </span>

          {/* While stowed, the bar shows a loud "TAP TO OPEN" cue so the affordance
              is unmissable; it lives in the same auto-margin slot the launcher uses
              when expanded. aria-hidden because the handle itself is the button. */}
          {collapsed ? (
            <span className="pdock-open-cue" aria-hidden="true">
              TAP TO OPEN
              <span className="pdock-open-cue-chev">&#9650;</span>
            </span>
          ) : (
            /* LAUNCHER — opens the Seraphim Bumper's own full-screen view. It does
               NOT mount the bumper inside the dock. */
            <button
              type="button"
              className="pdock-launch"
              onClick={launchBumper}
              title="Launch the Seraphim Bumper (full-screen)"
              aria-label="Launch the Seraphim Bumper full-screen experience"
            >
              SERAPHIM BUMPER <span className="pdock-launch-arrow" aria-hidden="true">&#9656;</span>
            </button>
          )}

          {/* Big labelled toggle pad — replaces the old tiny circular button.
              Generous hit area + a word so it reads clearly in both states. */}
          <button
            type="button"
            className="pdock-min"
            onClick={onToggleClick}
            aria-label={collapsed ? "Open music dock" : "Minimize music dock"}
            aria-expanded={!collapsed}
            title={collapsed ? "Open" : "Minimize"}
          >
            <span className="pdock-min-chev" aria-hidden="true">
              {collapsed ? "▲" : "▼"}
            </span>
            <span className="pdock-min-label">
              {collapsed ? "OPEN" : "HIDE"}
            </span>
          </button>

          <span className="pdock-rivot pdock-rivot-r" aria-hidden="true" />
        </div>

        {/* The body hosts ONLY StaticFM. It stays MOUNTED in both states so
            minimizing never tears down the audio engine / bus claim / meter —
            playback continues while stowed. When collapsed we add a modifier
            class (CSS shrinks the housing to the small minimized face) and hand
            StaticFM `compact` so it renders its MINIMIZED representation instead
            of the full cabinet. We never `return null` the audio component. */}
        <div className={"pdock-body" + (collapsed ? " is-mini" : "")}>
          {/* StaticFM: the compact default. It receives the global `sound`
              toggle and manages the audio bus itself. `compact` flips it to its
              small minimized face while the dock is stowed. */}
          <div
            className="pdock-panel pdock-panel-fm"
            role="region"
            aria-label="Static FM radio"
          >
            <StaticFM sound={sound} compact={collapsed} />
          </div>
        </div>
      </div>
    </Draggable>
  );
}

export default PlayerDock;
